import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Inbox, Send, FileText, FolderOpen, Printer, SlidersHorizontal, ChevronUp } from 'lucide-react';
import useApi from '../hooks/useApi';
import Table from '../components/ui/Table';
import Pagination from '../components/ui/Pagination';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import Combobox from '../components/ui/Combobox';
import SearchInput from '../components/ui/SearchInput';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { formatDate } from '../utils/helpers';
import { fetchAllPaginatedRows } from '../utils/paginatedExport';
import { exportToPDF } from '../utils/exportUtils';
import { useUIStore } from '../store/useUIStore';
import toast from 'react-hot-toast';

const Reports = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { settings, loading: settingsLoading } = useSettings();
  const { tableDensity, setTableDensity } = useUIStore();
  const { get } = useApi();
  const isEfileEnabled = settingsLoading ? true : (settings?.efile_enabled == 1 || settings?.efile_enabled === 'true' || settings?.efile_enabled === true);

  const role = String(user?.role || '');
  const roleLower = role.toLowerCase();
  const canChooseOffice = roleLower === 'superadmin' || roleLower === 'director' || roleLower === 'head' || roleLower === 'gm';

  const [activeTab, setActiveTab] = useState('diary');
  const [offices, setOffices] = useState([]);

  const [dispatchFilters, setDispatchFilters] = useState({
    officeId: '',
    dateFrom: '',
    dateTo: '',
    dispatchNumber: '',
    status: '',
    priority: '',
    modeOfDispatch: '',
    search: ''
  });

  const [diaryFilters, setDiaryFilters] = useState({
    officeId: '',
    dateFrom: '',
    dateTo: '',
    diaryNumber: '',
    status: '',
    priority: '',
    documentType: '',
    forwardedTo: '',
    search: ''
  });

  const [efileFilters, setEfileFilters] = useState({
    officeId: '',
    dateFrom: '',
    dateTo: '',
    fileNumber: '',
    status: '',
    search: ''
  });

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [lastUpdatedAt, setLastUpdatedAt] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'dateIssued', direction: 'desc' });
  const [visibleReportColumns, setVisibleReportColumns] = useState([]);
  const [tableReportColumnWidths, setTableReportColumnWidths] = useState({});
  const [showMoreFilters, setShowMoreFilters] = useState(false);
  const didInitTab = useRef(false);

  useEffect(() => {
    if (didInitTab.current) return;
    if (settingsLoading) return;
    if (!user?.role) return;
    const access = settings?.role_page_access;
    if (!access || typeof access !== 'object') return;
    const normalizeRole = (v) => String(v || '').trim().toLowerCase().replace(/\s+/g, ' ');
    const roleNorm = normalizeRole(user.role);
    const roleKey = Object.keys(access).find(k => normalizeRole(k) === roleNorm) || user.role;
    const allowedRaw = access?.[roleKey];
    const allowed = Array.isArray(allowedRaw) ? allowedRaw : null;
    if (!allowed) return;

    const canDispatch = allowed.includes('dispatch_list') || allowed.includes('dispatch_preview') || allowed.includes('dispatch_create') || allowed.includes('dispatch_edit');
    const canDiary = allowed.includes('diary_list') || allowed.includes('diary_preview') || allowed.includes('diary_create') || allowed.includes('diary_edit');
    const canEfile = isEfileEnabled && (allowed.includes('efile_workspace') || allowed.includes('efile_my_desk'));

    if (canDispatch) {
      setActiveTab('dispatch');
      didInitTab.current = true;
      return;
    }
    if (canDiary) {
      setActiveTab('diary');
      didInitTab.current = true;
      return;
    }
    if (canEfile) {
      setActiveTab('efile');
      didInitTab.current = true;
    }
  }, [isEfileEnabled, settings?.role_page_access, settingsLoading, user?.role]);

  useEffect(() => {
    (async () => {
      if (!user?.officeId) return;
      if (!canChooseOffice) {
        const row = { id: user.officeId, name: user.officeName || user.officeCode || 'My Office' };
        setOffices([row]);
        setDispatchFilters(prev => ({ ...prev, officeId: String(row.id) }));
        setDiaryFilters(prev => ({ ...prev, officeId: String(row.id) }));
        setEfileFilters(prev => ({ ...prev, officeId: String(row.id) }));
        return;
      }

      try {
        const res = await get('/offices');
        const rows = Array.isArray(res?.data) ? res.data : [];
        setOffices(rows);
      } catch (e) {
        void e;
        const row = { id: user.officeId, name: user.officeName || user.officeCode || 'My Office' };
        setOffices([row]);
      }
    })();
  }, [canChooseOffice, get, user?.officeCode, user?.officeId, user?.officeName]);

  useEffect(() => {
    setPage(1);
  }, [activeTab]);

  const officeOptions = useMemo(() => {
    const opts = offices.map(o => ({ value: String(o.id), label: o.name }));
    return canChooseOffice ? [{ value: '', label: 'All Offices' }, ...opts] : opts;
  }, [canChooseOffice, offices]);

  const dispatchStatusOptions = useMemo(() => {
    const base = Array.isArray(settings?.dispatch_statuses) ? settings.dispatch_statuses : [];
    return [{ value: '', label: 'All Status' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.dispatch_statuses]);

  const receiptStatusOptions = useMemo(() => {
    const base = Array.isArray(settings?.receipt_statuses) ? settings.receipt_statuses : [];
    return [{ value: '', label: 'All Status' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.receipt_statuses]);

  const dispatchPriorityOptions = useMemo(() => {
    const base = Array.isArray(settings?.dispatch_priorities) ? settings.dispatch_priorities : [];
    return [{ value: '', label: 'All Priority' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.dispatch_priorities]);

  const receiptPriorityOptions = useMemo(() => {
    const base = Array.isArray(settings?.receipt_priorities) ? settings.receipt_priorities : [];
    return [{ value: '', label: 'All Priority' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.receipt_priorities]);

  const receiptDocumentTypeOptions = useMemo(() => {
    const base = Array.isArray(settings?.receipt_document_types) ? settings.receipt_document_types : ['Letter', 'Noting', 'Email', 'Fax'];
    return [{ value: '', label: 'All Document Types' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.receipt_document_types]);

  const dispatchModeOptions = useMemo(() => {
    const base = Array.isArray(settings?.dispatch_modes) ? settings.dispatch_modes : [];
    return [{ value: '', label: 'All Modes' }, ...base.map(v => ({ value: v, label: v }))];
  }, [settings?.dispatch_modes]);

  const currentFilters = useMemo(() => {
    if (activeTab === 'dispatch') return dispatchFilters;
    if (activeTab === 'diary') return diaryFilters;
    if (activeTab === 'efile') return efileFilters;
    return dispatchFilters;
  }, [activeTab, diaryFilters, dispatchFilters, efileFilters]);

  const getVisibleColumns = useCallback((list = []) => {
    const key = (column) => String(column.header || column.accessorKey || '').trim();
    return list.filter((column) => {
      const match = visibleReportColumns.find((item) => item.key === key(column));
      return match ? match.visible : column.visible !== false;
    });
  }, [visibleReportColumns]);

  const getExportColumnWidths = useCallback((exportColumns = []) => {
    const widths = {};
    exportColumns.forEach((column, index) => {
      const key = String(column.header || column.accessorKey || '').trim();
      const width = Number(tableReportColumnWidths[key] || 0);
      if (width > 0) {
        widths[index] = { wch: Math.min(Math.max(width / 8, 12), 40) };
      }
    });
    return widths;
  }, [tableReportColumnWidths]);

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    params.set('page', String(page));
    params.set('limit', '20');
    if (sortConfig?.key) params.set('sortBy', sortConfig.key);
    if (sortConfig?.direction) params.set('order', sortConfig.direction.toUpperCase());

    Object.entries(currentFilters || {}).forEach(([k, v]) => {
      if (v === undefined || v === null) return;
      const s = String(v);
      if (s.trim() === '') return;
      params.set(k, s);
    });
    return params.toString();
  }, [activeTab, currentFilters, page, sortConfig]);

  const fetchAllRowsForExport = useCallback(async () => {
    const endpoint = activeTab === 'dispatch' ? '/dispatch' : (activeTab === 'diary' ? '/receipt' : '/efile/report');
    return fetchAllPaginatedRows({
      fetchPage: (currentPage) => {
      const params = new URLSearchParams();
        params.set('page', String(currentPage));
        params.set('limit', '200');
        if (sortConfig?.key) params.set('sortBy', sortConfig.key);
        if (sortConfig?.direction) params.set('order', sortConfig.direction.toUpperCase());
        Object.entries(currentFilters || {}).forEach(([k, v]) => {
          if (v === undefined || v === null) return;
          const s = String(v);
          if (s.trim() === '') return;
          params.set(k, s);
        });

        return get(`${endpoint}?${params.toString()}`);
      }
    });
  }, [activeTab, currentFilters, get, sortConfig?.direction, sortConfig?.key]);

  const fetchReport = useCallback(async () => {
    setLoading(true);
    try {
      const qs = buildQuery();
      const endpoint = activeTab === 'dispatch' ? '/dispatch' : (activeTab === 'diary' ? '/receipt' : '/efile/report');
      const res = await get(`${endpoint}?${qs}`);
      const rows = Array.isArray(res?.data) ? res.data : [];
      setData(rows);
      const tp = res?.pagination?.totalPages || 1;
      const tot = res?.pagination?.total || 0;
      setTotalPages(tp);
      setTotal(tot);
      setLastUpdatedAt(new Date());
    } catch (e) {
      const status = e?.response?.status;
      if (status === 403) {
        if (activeTab === 'dispatch') {
          setActiveTab('diary');
          return;
        }
        if (activeTab === 'diary' && isEfileEnabled) {
          setActiveTab('efile');
          return;
        }
      }
      setData([]);
      setTotalPages(1);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [activeTab, buildQuery, get]);

  useEffect(() => {
    fetchReport();
  }, [fetchReport]);

  const handleSort = (key) => {
    setSortConfig(current => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const activeFilterCount = useMemo(() => {
    const filtered = Object.entries(currentFilters || {}).filter(([, v]) => {
      if (v === undefined || v === null) return false;
      return String(v).trim() !== '';
    });
    return filtered.length;
  }, [currentFilters]);

  const clearActiveFilters = useCallback(() => {
    const fixedOfficeId = canChooseOffice ? '' : String(user?.officeId || '');
    if (activeTab === 'dispatch') {
      setDispatchFilters({
        officeId: fixedOfficeId,
        dateFrom: '',
        dateTo: '',
        dispatchNumber: '',
        status: '',
        priority: '',
        modeOfDispatch: '',
        search: ''
      });
      setSortConfig({ key: 'dateIssued', direction: 'desc' });
    } else if (activeTab === 'diary') {
      setDiaryFilters({
        officeId: fixedOfficeId,
        dateFrom: '',
        dateTo: '',
        diaryNumber: '',
        status: '',
        priority: '',
        documentType: '',
        forwardedTo: '',
        search: ''
      });
      setSortConfig({ key: 'dateReceived', direction: 'desc' });
    } else {
      setEfileFilters({
        officeId: fixedOfficeId,
        dateFrom: '',
        dateTo: '',
        fileNumber: '',
        status: '',
        search: ''
      });
      setSortConfig({ key: 'createdAt', direction: 'desc' });
    }
    setPage(1);
  }, [activeTab, canChooseOffice, user?.officeId]);

  const dispatchColumns = useMemo(() => ([
    { header: 'Dispatch #', accessorKey: 'dispatchNumber', className: 'w-40', cellClassName: 'w-40', sortable: true, render: (row) => <span className="font-medium">{row.dispatchNumber || '-'}</span> },
    { header: 'Subject', accessorKey: 'subject', className: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', cellClassName: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', noWrap: false, sortable: true, render: (row) => <span title={row.subject || ''} className="block leading-snug break-words [overflow-wrap:anywhere]">{row.subject || '-'}</span> },
    { header: 'Recipient', accessorKey: 'recipient', className: 'w-44', cellClassName: 'w-44', sortable: true, render: (row) => row.recipient || '-' },
    { header: 'Date', accessorKey: 'dateIssued', className: 'w-32', cellClassName: 'w-32', sortable: true, render: (row) => formatDate(row.dateIssued) },
    { header: 'Remarks', accessorKey: 'remarks', sortable: false, className: 'hidden 2xl:table-cell 2xl:w-[18rem]', cellClassName: 'hidden 2xl:table-cell 2xl:w-[18rem]', noWrap: false, render: (row) => <span title={row.remarks || ''} className="block leading-snug break-words [overflow-wrap:anywhere]">{row.remarks || '-'}</span> },
    { header: 'Status', accessorKey: 'status', sortable: true, render: (row) => row.status || '-' },
    { header: 'Office', accessorKey: 'officeName', className: 'hidden lg:table-cell w-44', cellClassName: 'hidden lg:table-cell w-44', sortable: true, render: (row) => row.officeName || '-' }
  ]), []);

  const diaryColumns = useMemo(() => ([
    { header: 'Diary #', accessorKey: 'diaryNumber', className: 'w-40', cellClassName: 'w-40', sortable: true, render: (row) => <span className="font-medium">{row.diaryNumber || '-'}</span> },
    { header: 'Subject', accessorKey: 'subject', sortable: true, className: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', cellClassName: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', noWrap: false, render: (row) => <span title={row.subject || ''} className="block leading-snug break-words [overflow-wrap:anywhere]">{row.subject || '-'}</span> },
    { header: 'Sender', accessorKey: 'sender', className: 'w-44', cellClassName: 'w-44', sortable: true, render: (row) => row.sender || '-' },
    { header: 'Document Type', accessorKey: 'documentType', className: 'hidden xl:table-cell w-36', cellClassName: 'hidden xl:table-cell w-36', sortable: true, render: (row) => row.documentType || 'Letter' },
    { header: 'Forwarded To', accessorKey: 'forwardedToName', className: 'hidden xl:table-cell w-44', cellClassName: 'hidden xl:table-cell w-44', sortable: true, render: (row) => row.forwardedToName || row.createdByName || 'Central Registry' },
    { header: 'Date', accessorKey: 'dateReceived', className: 'w-32', cellClassName: 'w-32', sortable: true, render: (row) => formatDate(row.dateReceived) },
    { header: 'Remarks', accessorKey: 'remarks', sortable: false, className: 'hidden 2xl:table-cell 2xl:w-[18rem]', cellClassName: 'hidden 2xl:table-cell 2xl:w-[18rem]', noWrap: false, render: (row) => <span title={row.remarks || ''} className="block leading-snug break-words [overflow-wrap:anywhere]">{row.remarks || '-'}</span> },
    { header: 'Status', accessorKey: 'status', sortable: true, render: (row) => row.status || '-' },
    { header: 'Office', accessorKey: 'officeName', className: 'hidden lg:table-cell w-44', cellClassName: 'hidden lg:table-cell w-44', sortable: true, render: (row) => row.officeName || '-' }
  ]), []);

  const efileColumns = useMemo(() => ([
    { header: 'File #', accessorKey: 'fileNumber', className: 'w-40', cellClassName: 'w-40', sortable: true, render: (row) => <span className="font-medium">{row.fileNumber || '-'}</span> },
    { header: 'Subject', accessorKey: 'subject', sortable: true, className: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', cellClassName: 'w-[16rem] sm:w-[18rem] lg:w-[22rem]', noWrap: false, render: (row) => <span title={row.subject || ''} className="block leading-snug break-words [overflow-wrap:anywhere]">{row.subject || '-'}</span> },
    { header: 'Current Desk', accessorKey: 'currentDeskName', className: 'hidden lg:table-cell w-44', cellClassName: 'hidden lg:table-cell w-44', sortable: false, render: (row) => row.currentDeskName || '-' },
    { header: 'Created By', accessorKey: 'createdByName', className: 'hidden xl:table-cell w-44', cellClassName: 'hidden xl:table-cell w-44', sortable: false, render: (row) => row.createdByName || '-' },
    { header: 'Created', accessorKey: 'createdAt', className: 'w-32', cellClassName: 'w-32', sortable: true, render: (row) => formatDate(row.createdAt) },
    { header: 'Status', accessorKey: 'status', sortable: true, render: (row) => row.status || '-' },
    { header: 'Office', accessorKey: 'officeName', className: 'hidden lg:table-cell w-44', cellClassName: 'hidden lg:table-cell w-44', sortable: true, render: (row) => row.officeName || '-' }
  ]), []);

  const columns = activeTab === 'dispatch' ? dispatchColumns : (activeTab === 'diary' ? diaryColumns : efileColumns);

  const exportColumns = useMemo(() => {
    if (activeTab === 'dispatch') {
      return [
        { header: 'Dispatch #', accessorKey: 'dispatchNumber' },
        { header: 'Subject', accessorKey: 'subject' },
        { header: 'Recipient', accessorKey: 'recipient' },
        { header: 'Date', accessorKey: (row) => formatDate(row.dateIssued) },
        { header: 'Remarks', accessorKey: 'remarks' },
        { header: 'Status', accessorKey: 'status' },
        { header: 'Office', accessorKey: 'officeName' }
      ];
    }
    if (activeTab === 'diary') {
      return [
        { header: 'Diary #', accessorKey: 'diaryNumber' },
        { header: 'Subject', accessorKey: 'subject' },
        { header: 'Sender', accessorKey: 'sender' },
        { header: 'Document Type', accessorKey: (row) => row.documentType || 'Letter' },
        { header: 'Forwarded To', accessorKey: (row) => row.forwardedToName || row.createdByName || 'Central Registry' },
        { header: 'Date', accessorKey: (row) => formatDate(row.dateReceived) },
        { header: 'Remarks', accessorKey: 'remarks' },
        { header: 'Status', accessorKey: 'status' },
        { header: 'Office', accessorKey: 'officeName' }
      ];
    }
    return [
      { header: 'File #', accessorKey: 'fileNumber' },
      { header: 'Subject', accessorKey: 'subject' },
      { header: 'Current Desk', accessorKey: 'currentDeskName' },
      { header: 'Created By', accessorKey: 'createdByName' },
      { header: 'Created', accessorKey: (row) => formatDate(row.createdAt) },
      { header: 'Status', accessorKey: 'status' },
      { header: 'Office', accessorKey: 'officeName' }
    ];
  }, [activeTab]);

  const resolveOfficeName = useCallback((officeId) => {
    if (!officeId) return '';
    const row = (offices || []).find(o => String(o.id) === String(officeId));
    return row?.name || '';
  }, [offices]);

  const reportOfficeName = useMemo(() => {
    if (!canChooseOffice) return user?.officeName || 'Office Name';
    if (!currentFilters.officeId) return user?.officeName || 'Office Name';
    return resolveOfficeName(currentFilters.officeId) || user?.officeName || 'Office Name';
  }, [canChooseOffice, currentFilters.officeId, resolveOfficeName, user?.officeName]);

  const handleDownloadPDF = useCallback(async () => {
    try {
      const title = activeTab === 'dispatch' ? 'Dispatch Report' : (activeTab === 'diary' ? 'Diary Report' : 'e-File Report');
      const rows = await fetchAllRowsForExport();
      const visibleExportColumns = getVisibleColumns(exportColumns);
      await exportToPDF(rows, visibleExportColumns, title, {
        orientation: 'landscape',
        officeName: reportOfficeName,
        startDate: currentFilters.dateFrom,
        endDate: currentFilters.dateTo,
        output: 'download',
        columnWidths: {
          ...getExportColumnWidths(visibleExportColumns),
          ...(activeTab === 'diary' ? {
            1: { cellWidth: 32 },
            6: { cellWidth: 32 }
          } : {})
        }
      });
      toast.success('PDF exported successfully');
    } catch {
      toast.error('Failed to export PDF');
    }
  }, [activeTab, currentFilters.dateFrom, currentFilters.dateTo, exportColumns, fetchAllRowsForExport, getVisibleColumns, reportOfficeName]);

  const handlePrint = useCallback(async () => {
    try {
      const title = activeTab === 'dispatch' ? 'Dispatch Report' : (activeTab === 'diary' ? 'Diary Report' : 'e-File Report');
      const rows = await fetchAllRowsForExport();
      const visibleExportColumns = getVisibleColumns(exportColumns);
      const result = await exportToPDF(rows, visibleExportColumns, title, {
        orientation: 'landscape',
        officeName: reportOfficeName,
        startDate: currentFilters.dateFrom,
        endDate: currentFilters.dateTo,
        output: 'print',
        columnWidths: {
          ...getExportColumnWidths(visibleExportColumns),
          ...(activeTab === 'diary' ? {
            1: { cellWidth: 32 },
            6: { cellWidth: 32 }
          } : {})
        }
      });
      if (result?.mode === 'print-fallback-download') {
        toast.success('Popup blocked. PDF downloaded instead.');
      } else {
        toast.success('Print preview opened');
      }
    } catch {
      toast.error('Failed to print');
    }
  }, [activeTab, currentFilters.dateFrom, currentFilters.dateTo, exportColumns, fetchAllRowsForExport, getVisibleColumns, reportOfficeName]);

  return (
    <div className="flex flex-col flex-1 min-h-0">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7 text-indigo-600" />
            Reports
          </h1>
          <p className="text-gray-500 mt-1">Filter by date range, number, status, and office.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1">
            <Button
              size="sm"
              variant={tableDensity === 'comfortable' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('comfortable')}
            >
              Comfortable
            </Button>
            <Button
              size="sm"
              variant={tableDensity === 'compact' ? 'primary' : 'ghost'}
              onClick={() => setTableDensity('compact')}
            >
              Compact
            </Button>
          </div>
          <Button variant="secondary" onClick={handleDownloadPDF}>
            <FileText className="w-4 h-4 mr-2 text-red-500" />
            PDF
          </Button>
          <Button variant="secondary" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
          <Button variant="outline" onClick={clearActiveFilters}>
            Clear Filters
          </Button>
          <Button onClick={fetchReport} isLoading={loading}>Refresh</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="rounded-xl border border-indigo-100 bg-indigo-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Rows</p>
          <p className="text-lg font-bold text-indigo-900">{data.length} / {total}</p>
        </div>
        <div className="rounded-xl border border-amber-100 bg-amber-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-amber-700">Active Filters</p>
          <p className="text-lg font-bold text-amber-900">{activeFilterCount}</p>
        </div>
        <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-3">
          <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Last Refresh</p>
          <p className="text-lg font-bold text-emerald-900">{lastUpdatedAt ? lastUpdatedAt.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : '-'}</p>
        </div>
      </div>

      <div className="flex gap-2 mb-4 flex-wrap">
        <Button
          variant={activeTab === 'dispatch' ? 'default' : 'outline'}
          onClick={() => {
            setActiveTab('dispatch');
            setSortConfig({ key: 'dateIssued', direction: 'desc' });
          }}
          icon={Send}
        >
          Dispatch
        </Button>
        <Button
          variant={activeTab === 'diary' ? 'default' : 'outline'}
          onClick={() => {
            setActiveTab('diary');
            setSortConfig({ key: 'dateReceived', direction: 'desc' });
          }}
          icon={Inbox}
        >
          Diary
        </Button>
        {isEfileEnabled && (
          <Button
            variant={activeTab === 'efile' ? 'default' : 'outline'}
            onClick={() => {
              setActiveTab('efile');
              setSortConfig({ key: 'createdAt', direction: 'desc' });
            }}
            icon={FolderOpen}
          >
            e-File
          </Button>
        )}
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 sm:p-4 mb-6">
        <div className="flex items-center justify-end mb-3">
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowMoreFilters(prev => !prev)}
            className="inline-flex items-center gap-2 rounded-full border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700"
          >
            {showMoreFilters ? <ChevronUp className="h-3.5 w-3.5" /> : <SlidersHorizontal className="h-3.5 w-3.5" />}
            {showMoreFilters ? 'Hide Filters' : 'More Filters'}
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-end">
          <div className="lg:col-span-3">
            <Input
              type="date"
              label="Date From"
              value={currentFilters.dateFrom}
              onChange={(e) => {
                const v = e.target.value;
                if (activeTab === 'dispatch') setDispatchFilters(prev => ({ ...prev, dateFrom: v }));
                else if (activeTab === 'diary') setDiaryFilters(prev => ({ ...prev, dateFrom: v }));
                else if (activeTab === 'efile') setEfileFilters(prev => ({ ...prev, dateFrom: v }));
                setPage(1);
              }}
            />
          </div>
          <div className="lg:col-span-3">
            <Input
              type="date"
              label="Date To"
              value={currentFilters.dateTo}
              onChange={(e) => {
                const v = e.target.value;
                if (activeTab === 'dispatch') setDispatchFilters(prev => ({ ...prev, dateTo: v }));
                else if (activeTab === 'diary') setDiaryFilters(prev => ({ ...prev, dateTo: v }));
                else if (activeTab === 'efile') setEfileFilters(prev => ({ ...prev, dateTo: v }));
                setPage(1);
              }}
            />
          </div>

          <div className="lg:col-span-3">
            <Combobox
              label="Office"
              options={officeOptions}
              value={currentFilters.officeId}
              onChange={(v) => {
                if (activeTab === 'dispatch') setDispatchFilters(prev => ({ ...prev, officeId: v }));
                else if (activeTab === 'diary') setDiaryFilters(prev => ({ ...prev, officeId: v }));
                else if (activeTab === 'efile') setEfileFilters(prev => ({ ...prev, officeId: v }));
                setPage(1);
              }}
              placeholder="Select office..."
            />
          </div>

          <div className="lg:col-span-3">
            <SearchInput
              value={
                activeTab === 'dispatch'
                  ? dispatchFilters.search
                  : activeTab === 'diary'
                    ? diaryFilters.search
                    : efileFilters.search
              }
              onChange={(v) => {
                if (activeTab === 'dispatch') {
                  setDispatchFilters(prev => ({ ...prev, search: v }));
                } else if (activeTab === 'diary') {
                  setDiaryFilters(prev => ({ ...prev, search: v }));
                } else {
                  setEfileFilters(prev => ({ ...prev, search: v }));
                }
                setPage(1);
              }}
              onClear={() => {
                if (activeTab === 'dispatch') setDispatchFilters(prev => ({ ...prev, search: '' }));
                else if (activeTab === 'diary') setDiaryFilters(prev => ({ ...prev, search: '' }));
                else setEfileFilters(prev => ({ ...prev, search: '' }));
              }}
              placeholder={activeTab === 'dispatch' ? 'Search subject/recipient...' : activeTab === 'diary' ? 'Search subject/sender/reference...' : 'Search file number/subject...'}
            />
          </div>

          {showMoreFilters && activeTab === 'dispatch' && (
            <>
              <div className="lg:col-span-4">
                <SearchInput
                  value={dispatchFilters.dispatchNumber}
                  onChange={(v) => {
                    setDispatchFilters(prev => ({ ...prev, dispatchNumber: v }));
                    setPage(1);
                  }}
                  onClear={() => setDispatchFilters(prev => ({ ...prev, dispatchNumber: '' }))}
                  placeholder="Dispatch number..."
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Status"
                  options={dispatchStatusOptions}
                  value={dispatchFilters.status}
                  onChange={(v) => {
                    setDispatchFilters(prev => ({ ...prev, status: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Priority"
                  options={dispatchPriorityOptions}
                  value={dispatchFilters.priority}
                  onChange={(v) => {
                    setDispatchFilters(prev => ({ ...prev, priority: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Mode"
                  options={dispatchModeOptions}
                  value={dispatchFilters.modeOfDispatch}
                  onChange={(v) => {
                    setDispatchFilters(prev => ({ ...prev, modeOfDispatch: v }));
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}

          {showMoreFilters && activeTab === 'diary' && (
            <>
              <div className="lg:col-span-4">
                <SearchInput
                  value={diaryFilters.diaryNumber}
                  onChange={(v) => {
                    setDiaryFilters(prev => ({ ...prev, diaryNumber: v }));
                    setPage(1);
                  }}
                  onClear={() => setDiaryFilters(prev => ({ ...prev, diaryNumber: '' }))}
                  placeholder="Diary number..."
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Status"
                  options={receiptStatusOptions}
                  value={diaryFilters.status}
                  onChange={(v) => {
                    setDiaryFilters(prev => ({ ...prev, status: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Priority"
                  options={receiptPriorityOptions}
                  value={diaryFilters.priority}
                  onChange={(v) => {
                    setDiaryFilters(prev => ({ ...prev, priority: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Document Type"
                  options={receiptDocumentTypeOptions}
                  value={diaryFilters.documentType}
                  onChange={(v) => {
                    setDiaryFilters(prev => ({ ...prev, documentType: v }));
                    setPage(1);
                  }}
                />
              </div>
              <div className="lg:col-span-4">
                <SearchInput
                  value={diaryFilters.forwardedTo}
                  onChange={(v) => {
                    setDiaryFilters(prev => ({ ...prev, forwardedTo: v }));
                    setPage(1);
                  }}
                  onClear={() => setDiaryFilters(prev => ({ ...prev, forwardedTo: '' }))}
                  placeholder="Forwarded to..."
                />
              </div>
            </>
          )}

          {showMoreFilters && activeTab === 'efile' && (
            <>
              <div className="lg:col-span-4">
                <SearchInput
                  value={efileFilters.fileNumber}
                  onChange={(v) => {
                    setEfileFilters(prev => ({ ...prev, fileNumber: v }));
                    setPage(1);
                  }}
                  onClear={() => setEfileFilters(prev => ({ ...prev, fileNumber: '' }))}
                  placeholder="File number..."
                />
              </div>
              <div className="lg:col-span-4">
                <Combobox
                  label="Status"
                  options={[
                    { value: '', label: 'All Status' },
                    { value: 'Active', label: 'Active' },
                    { value: 'Closed', label: 'Closed' }
                  ]}
                  value={efileFilters.status}
                  onChange={(v) => {
                    setEfileFilters(prev => ({ ...prev, status: v }));
                    setPage(1);
                  }}
                />
              </div>
            </>
          )}
        </div>
      </div>

      <div className="flex-1 min-h-0">
        <Table
          columns={columns}
          data={data}
          sortConfig={sortConfig}
          onSort={handleSort}
          onRowClick={(row) => {
            if (activeTab === 'dispatch') navigate(`/dispatch/${row.id}/view`);
            else if (activeTab === 'diary') navigate(`/diary/${row.id}/view`);
            else navigate(`/efile/${row.id}`);
          }}
          className={loading ? 'opacity-60 pointer-events-none' : ''}
          tableClassName="table-fixed"
          density={tableDensity}
          enableColumnVisibility
          enableColumnResize
          showColumnControls
          onColumnVisibilityChange={setVisibleReportColumns}
          onColumnResizeChange={setTableReportColumnWidths}
        />
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={setPage}
          totalItems={total}
          itemsPerPage={20}
        />
      </div>
    </div>
  );
};

export default Reports;
